/**
 * LifetimePrompt Component
 * 
 * Contextual prompt for Lifetime access shown at key moments:
 * - Timeline: When user's world starts connecting (7-day cooldown)
 * - Members: First visit to Members tab (once per user, permanent)
 * 
 * Non-blocking inline panel or side drawer. Does not interrupt workflow.
 * Copy from Master Plan Sections 8.1 and 8.2.
 */

import { motion, AnimatePresence } from 'framer-motion'
import { Icon } from '../common/Icon'
import { useNavigate } from 'react-router-dom'
import './LifetimePrompt.css'

// Approved copy from Master Plan
const COPY = {
  timeline: {
    headline: 'Your world is starting to connect.',
    body: 'Lifetime access removes all limits as it grows.',
    primaryCta: 'View Lifetime Access',
    secondaryCta: 'Not now',
  },
  members: {
    headline: 'Working with others?',
    body: 'Lifetime access includes unlimited projects, priority support, and shared project access for your team.',
    subline: 'Built for collaborators and studios.',
    primaryCta: 'See Lifetime Benefits',
    secondaryCta: 'Continue with current plan',
  },
}

// Animation variants
const promptVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    scale: 0.95,
  },
  animate: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    }
  },
  exit: { 
    opacity: 0, 
    y: -10,
    scale: 0.98,
    transition: {
      duration: 0.2,
    }
  },
}

/**
 * LifetimePrompt
 * 
 * @param {Object} props
 * @param {'timeline' | 'members'} props.variant - Which copy variant to use
 * @param {boolean} props.isVisible - Whether the prompt is visible
 * @param {Function} props.onDismiss - Callback when dismissed
 * @param {Function} props.onViewLifetime - Optional callback for view action (defaults to navigation)
 */
export function LifetimePrompt({
  variant = 'timeline',
  isVisible = true,
  onDismiss,
  onViewLifetime,
}) {
  const navigate = useNavigate()
  const copy = COPY[variant] || COPY.timeline

  const handleViewLifetime = () => {
    if (onViewLifetime) {
      onViewLifetime()
    } else {
      // Default: navigate to payment view with lifetime tab
      navigate('/payment?tab=lifetime')
    }
    // Also dismiss the prompt
    onDismiss?.()
  }

  const handleDismiss = () => {
    onDismiss?.()
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={`lifetime-prompt lifetime-prompt--${variant}`}
          variants={promptVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          role="complementary"
          aria-label="Lifetime access information"
        >
          {/* Decorative glow */}
          <div className="lifetime-prompt__glow" />
          
          {/* Icon */}
          <div className="lifetime-prompt__icon">
            <Icon name="lifetime" size={24} />
          </div>

          {/* Content */}
          <div className="lifetime-prompt__content">
            <h3 className="lifetime-prompt__headline">{copy.headline}</h3>
            <p className="lifetime-prompt__body">{copy.body}</p>
            {copy.subline && (
              <p className="lifetime-prompt__subline">{copy.subline}</p>
            )}
          </div>

          {/* Actions */}
          <div className="lifetime-prompt__actions">
            <button
              className="lifetime-prompt__btn lifetime-prompt__btn--primary"
              onClick={handleViewLifetime}
            >
              {copy.primaryCta}
            </button>
            <button
              className="lifetime-prompt__btn lifetime-prompt__btn--secondary"
              onClick={handleDismiss}
            >
              {copy.secondaryCta}
            </button>
          </div>

          {/* Close button */}
          <button
            className="lifetime-prompt__close"
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            ×
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default LifetimePrompt
